"use client";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'next/navigation';
import { generateKey } from '../../../foundation/utilities';
import { usePixelatedConfig } from '../../../config/config.client';
import { componentMap, isLayoutComponent } from '../lib/componentMap';
import './pagebuilder.scss';
/* ========== PAGE DATA PROVIDER ========== */
/**
 * PageDataContext holds the dynamic state for the page, including siteConfig,
 * URL params, and any external data sources.
 */
const PageDataContext = createContext(null);
export function usePageData() {
    return useContext(PageDataContext);
}
/**
 * PageDataProvider — Wraps the PageEngine to provide a unified data context.
 *
 * @param {object} [props.data] - Additional static data to inject.
 * @param {object} [props.siteConfig] - The site-specific configuration (pixelated.config.json).
 */
PageDataProvider.propTypes = {
    siteConfig: PropTypes.object,
    data: PropTypes.object,
    children: PropTypes.node.isRequired,
};
export function PageDataProvider(props) {
    const { children, siteConfig, data = {} } = props;
    const params = useParams();
    const pixelatedConfig = usePixelatedConfig();
    // Merge all data sources into a single context
    const contextValue = useMemo(() => {
        const sc = siteConfig;
        return {
            siteInfo: sc?.siteInfo,
            business: sc?.siteInfo?.business,
            params: params || {},
            technical: pixelatedConfig,
            ...data
        };
    }, [siteConfig, params, pixelatedConfig, data]);
    return (_jsx(PageDataContext.Provider, { value: contextValue, children: children }));
}
/**
 * PageEngine - Renders components with optional inline editing
 * When editMode is true, shows borders, hover effects, and action buttons
 * When editMode is false (default), renders clean components without edit UI
 */
/**
 * PageEngine — Render page components (and their children) with optional inline editing functionality.
 *
 * @param {shape} [props.pageData] - Page JSON containing a `components` array describing the page structure.
 * @param {boolean} [props.editMode] - When true, show editing UI (borders, buttons) around components.
 * @param {string} [props.selectedPath] - Path string identifying the currently selected component.
 * @param {function} [props.onEditComponent] - Callback invoked to begin editing a component's properties.
 * @param {function} [props.onSelectComponent] - Callback to select a component for adding children.
 * @param {function} [props.onDeleteComponent] - Callback to delete a component at a given path.
 * @param {function} [props.onMoveUp] - Callback to move a component up within its siblings.
 * @param {function} [props.onMoveDown] - Callback to move a component down within its siblings.
 */
PageEngine.propTypes = {
    /** Page JSON with components array */
    pageData: PropTypes.shape({
        components: PropTypes.arrayOf(PropTypes.shape({
            component: PropTypes.string.isRequired,
            props: PropTypes.object.isRequired,
            children: PropTypes.array,
        })).isRequired,
    }).isRequired,
    /** Show inline edit UI when true */
    editMode: PropTypes.bool,
    /** Currently selected component path */
    selectedPath: PropTypes.string,
    /** Begin editing component properties */
    onEditComponent: PropTypes.func,
    /** Select a component for child insertion */
    onSelectComponent: PropTypes.func,
    /** Delete a component */
    onDeleteComponent: PropTypes.func,
    /** Move component up */
    onMoveUp: PropTypes.func,
    /** Move component down */
    onMoveDown: PropTypes.func,
};
export function PageEngine(props) {
    const { editMode = false, selectedPath, onEditComponent, onSelectComponent, onDeleteComponent, onMoveUp, onMoveDown } = props;
    const contextData = usePageData();
    // Helper to resolve {{token}} strings in props
    const resolveValue = (value) => {
        if (typeof value !== 'string')
            return value;
        // 1. If the value is EXACTLY a unique token like "{{some.path}}", 
        // we return the raw object/value instead of stringifying it.
        // This is critical for array/object props like faqsData.
        const fullTokenMatch = value.match(/^\{\{([\w.]+)\}\}$/);
        if (fullTokenMatch) {
            const path = fullTokenMatch[1];
            const parts = path.split('.');
            let current = contextData;
            for (const part of parts) {
                if (current && typeof current === 'object') {
                    current = current[part];
                }
                else {
                    return undefined;
                }
            }
            return current;
        }
        // 2. Otherwise, perform string replacement for mixed content
        const tokenRegex = /\{\{([\w.]+)\}\}/g;
        if (!tokenRegex.test(value))
            return value;
        return value.replace(tokenRegex, (_, path) => {
            const parts = path.split('.');
            let current = contextData;
            for (const part of parts) {
                if (current && typeof current === 'object') {
                    current = current[part];
                }
                else {
                    return `[undefined: ${path}]`;
                }
            }
            // If we're inside a string, stringify objects or return the primitive
            return current !== undefined
                ? (typeof current === 'object' ? JSON.stringify(current) : current)
                : `[undefined: ${path}]`;
        });
    };
    // Recursive function to render components with children
    function renderComponent(componentData, index, path = 'root') {
        const componentName = componentData.component;
        // Create a shallow copy of props and resolve tokens
        const rawProps = componentData.props || {};
        const componentProps = {};
        Object.keys(rawProps).forEach(key => {
            if (key !== 'type') {
                componentProps[key] = resolveValue(rawProps[key]);
            }
        });
        const componentType = componentMap[componentName];
        const currentPath = `${path}[${index}]`;
        const isLayout = isLayoutComponent(componentName);
        if (!componentType) {
            // If the component type is missing, still render its children so nested
            // components are visible in the page preview/tests. Also show a clear
            // unknown-component message for debugging.
            return (_jsxs("div", { children: [_jsxs("div", { children: ["Unknown component: ", componentName] }), componentData.children && componentData.children.length > 0 && (_jsx("div", { children: componentData.children.map((child, childIndex) => renderComponent(child, childIndex, `${currentPath}.children`)) }))] }, index));
        }
        // If component has children, recursively render them
        let children = null;
        if (componentData.children && componentData.children.length > 0) {
            children = componentData.children.map((child, childIndex) => renderComponent(child, childIndex, `${currentPath}.children`));
        }
        componentProps.key = generateKey();
        const element = children
            ? React.createElement(componentType, componentProps, children)
            : React.createElement(componentType, componentProps);
        // If not in edit mode, return element directly without wrapper
        if (!editMode) {
            return element; // Removed React.Fragment wrapper as we have unique keys on the elements now
        }
        // Edit mode: Wrap with hover effect and action buttons
        const isSelected = selectedPath === currentPath;
        const handleMouseEnter = (currentTarget, event) => {
            if (event.target === event.currentTarget || !currentTarget.querySelector('.pagebuilder-component-wrapper:hover')) {
                document.querySelectorAll('.pagebuilder-component-wrapper.hover-active').forEach(el => {
                    el.classList.remove('hover-active');
                });
                currentTarget.classList.add('hover-active');
            }
            event.stopPropagation();
        };
        const handleMouseLeave = (currentTarget, event) => {
            const relatedTarget = (event.relatedTarget || event.relatedTarget);
            if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
                currentTarget.classList.remove('hover-active');
            }
        };
        return (_jsxs("div", { className: `pagebuilder-component-wrapper ${isSelected ? 'selected' : ''}`, onMouseOver: (e) => {
                handleMouseEnter(e.currentTarget, e);
            }, onMouseOut: (e) => {
                handleMouseLeave(e.currentTarget, e);
            }, onFocus: (e) => {
                handleMouseEnter(e.currentTarget, e);
            }, onBlur: (e) => {
                handleMouseLeave(e.currentTarget, e);
            }, children: [element, _jsxs("div", { className: "pagebuilder-actions", children: [_jsxs("div", { className: "move-buttons", children: [_jsx("button", { className: "move-btn move-up", onClick: (e) => {
                                        e.stopPropagation();
                                        onMoveUp?.(currentPath);
                                    }, title: "Move up", children: "\u25B2" }), _jsx("button", { className: "move-btn move-down", onClick: (e) => {
                                        e.stopPropagation();
                                        onMoveDown?.(currentPath);
                                    }, title: "Move down", children: _jsx("span", { role: "img", "aria-label": "move down", children: "\u25BC" }) })] }), _jsx("button", { className: "edit-btn", onClick: (e) => {
                                e.stopPropagation();
                                onEditComponent?.(componentData, currentPath);
                            }, title: "Edit properties", children: _jsx("span", { role: "img", "aria-label": "edit", children: "\u270F\uFE0F" }) }), isLayout && (_jsx("button", { className: "child-btn", onClick: (e) => {
                                e.stopPropagation();
                                onSelectComponent?.(componentData, currentPath);
                            }, title: "Add child component", children: _jsx("span", { role: "img", "aria-label": "add", children: "\u2795" }) })), _jsx("button", { className: "delete-btn", onClick: (e) => {
                                e.stopPropagation();
                                onDeleteComponent?.(currentPath);
                            }, title: "Delete component", children: _jsx("span", { role: "img", "aria-label": "delete", children: "\uD83D\uDDD1\uFE0F" }) })] })] }, `wrapper-${index}`));
    }
    const components = [];
    const pageComponents = props?.pageData?.components;
    if (pageComponents) {
        pageComponents.forEach((component, index) => {
            components.push(renderComponent(component, index));
        });
    }
    return _jsx(_Fragment, { children: components });
}
